/**
 * CrossMatch drill strategy + kind helpers (Phase 3, design §1).
 * Pure: no Vue, no i18n, no effects. Ported from useCrossMatch; the HTML
 * builders escape container/item text (BUG-21).
 */
import type { StackCrossMatchItem } from 'src/types/components';
import type { CrossMatchTry, Try } from 'src/types/course/step';
import { itemsToArray, shuffleArray } from 'src/shared/utilities/array';
import { escapeText } from './escapeText';
import type { DrillResultRowMap, DrillStrategy, MatchingValue } from './types';

type CrossMatchItem = StackCrossMatchItem['attributes'];
/** What useCrossMatch accepts as the correct answer (matching_values). */
type CorrectAnswer = [Record<string | symbol, string>] | MatchingValue[];

/** The solve card's leftover pool key: legacy/stored answers carry it, never a container. */
export const CROSS_MATCH_POOL_KEY = 'itemsPool';

export type CrossMatchTryItem = { item: string; correct: boolean | undefined };
export type CrossMatchTryContainer = {
  container: string;
  items: CrossMatchTryItem[];
};

/** matching_values as { container, items[] } ('|' lists split + trimmed). */
export function crossMatchCorrectArray(correct: CorrectAnswer | undefined) {
  return (correct as Record<string | symbol, string>[] | undefined)?.map(
    (item) => ({
      container: item.container as string,
      items: itemsToArray(item.items),
    }),
  );
}

/** Is `item` one of `container`'s correct items? */
export function crossMatchItemCorrect(
  correct: CorrectAnswer | undefined,
  container: string,
  item: string,
) {
  return !!crossMatchCorrectArray(correct)?.find(
    (ca) => ca.container === container && ca.items.includes(item),
  );
}

/**
 * A try's answer per container: each placed item marked correct/incorrect,
 * then the container's missing correct items appended with `correct: undefined`.
 * No try, or a try without an object answer (help try, legacy string, null)
 * → [] (BUG-22: null no longer throws).
 */
export function crossMatchTryData(
  correct: CorrectAnswer | undefined,
  answerTry?: CrossMatchTry,
): CrossMatchTryContainer[] {
  if (!answerTry?.answer || typeof answerTry.answer !== 'object') return [];
  const correctArray = crossMatchCorrectArray(correct);
  return Object.keys(answerTry.answer).map((container) => {
    const items: CrossMatchTryItem[] =
      answerTry.answer[container]?.map((item) => ({
        item,
        correct: !!correctArray?.find(
          (ca) => ca.container === container && ca.items.includes(item),
        ),
      })) ?? [];

    correctArray
      ?.find((ca) => ca.container === container)
      ?.items.forEach((caItem) => {
        if (!items.find((i) => i.item === caItem)) {
          items.push({
            item: caItem,
            correct: undefined,
          });
        }
      });

    return {
      container,
      items,
    };
  });
}

/** "Your answer" HTML of a try (useCrossMatch.crossMatchFeedbackAnswer). */
export function crossMatchFeedbackHtml(
  correct: CorrectAnswer | undefined,
  answerTry?: CrossMatchTry,
) {
  // A legacy string answer is shown as its (escaped) text, as before.
  if (typeof answerTry?.answer === 'string')
    return escapeText(answerTry.answer);
  const data = crossMatchTryData(correct, answerTry);
  let feedbackString = '';
  data.forEach((container) => {
    feedbackString += `<p class="text-border"><span style="border: 1px solid;" class="text-bold rounded-borders q-px-xs ${container.items.some((i) => i.correct !== true) ? 'text-negative' : 'text-positive'}">${escapeText(container.container)}</span>`;
    container.items.forEach((item, index) => {
      feedbackString += `${index > 0 ? ', ' : ' '}`;
      if (item.correct === true) {
        feedbackString += `<span class="text-positive">${escapeText(item.item)}</span>`;
      } else if (item.correct === false) {
        feedbackString += `<span class="text-negative">${escapeText(item.item)}</span>`;
      } else {
        feedbackString += `<span>${escapeText(item.item)}</span>`;
      }
    });
    feedbackString += '</p>';
  });
  return feedbackString;
}

/** The correct answer as HTML (help dialog; useCrossMatch.correctAnswerString). */
export function crossMatchCorrectHtml(correct: CorrectAnswer | undefined) {
  let feedbackString = '';
  crossMatchCorrectArray(correct)?.forEach((container) => {
    feedbackString += `<p class="text-positive"><span style="border: 1px solid;" class="text-bold rounded-borders q-px-xs">${escapeText(container.container)}</span>`;
    container.items.forEach((item, index) => {
      feedbackString += `${index > 0 ? ', ' : ' '}`;
      feedbackString += `<span>${escapeText(item)}</span>`;
    });
    feedbackString += '</p>';
  });
  return feedbackString;
}

/**
 * The solve card's shuffled board. RNG order matches the card on mount: its
 * `items` computed is evaluated first (immediate watcher), then `containers`.
 */
export function crossMatchBoard(item: CrossMatchItem | undefined) {
  const matchingValues = (item?.matching_values ?? []) as MatchingValue[];
  if (!matchingValues.length) return { items: [], containers: [] };

  const matchingItems = matchingValues
    .map((c) => itemsToArray(c.items ?? ''))
    .flat();
  const items = shuffleArray(
    matchingItems.concat(itemsToArray(item?.dummy_items)),
  );

  const matchingContainers = matchingValues
    .map((c) => c.container ?? '')
    .filter(Boolean);
  const containers = shuffleArray(
    matchingContainers.concat(itemsToArray(item?.dummy_containers)),
  );

  return { items, containers };
}

function isCorrect(
  answer: Record<string | symbol, string[]>,
  correctAnswer: MatchingValue[] | undefined,
) {
  if (!correctAnswer || !answer) return false;

  const answerNoEmptyContainers = Object.keys(answer).reduce(
    (acc, container) => {
      if (container === CROSS_MATCH_POOL_KEY) return acc;
      if ((answer?.[container]?.length ?? 0) > 0) {
        acc[container] = answer?.[container] ?? [];
      }
      return acc;
    },
    {} as Record<string, string[]>,
  );

  if (Object.keys(answerNoEmptyContainers).length !== correctAnswer.length)
    return false;

  for (const container in answerNoEmptyContainers) {
    const correctContainer = correctAnswer.find(
      (item) => item.container === container,
    );

    if (!correctContainer) return false;

    const correctContainerItems = itemsToArray(correctContainer.items);

    if (
      !Array.isArray(answerNoEmptyContainers[container]) ||
      answerNoEmptyContainers[container].length !== correctContainerItems.length
    )
      return false;

    for (const item of answerNoEmptyContainers[container]) {
      if (!correctContainerItems.includes(item)) return false;
    }
  }

  return true;
}

/** useStep's comparison feedback: pool dropped, containers in the drill's order (BUG-6/BUG-19). */
function feedbackAnswerHtml(
  answer: Record<string, string[]> | undefined,
  correctAnswer: MatchingValue[] | undefined,
) {
  const raw = answer ?? {};
  const order = (correctAnswer ?? []).map((v) => v.container);
  const keys = [
    ...order.filter((k) => k in raw),
    ...Object.keys(raw).filter((k) => !order.includes(k)),
  ].filter((k) => k !== CROSS_MATCH_POOL_KEY);
  const placed = Object.fromEntries(keys.map((k) => [k, raw[k] ?? []]));
  const answerTry: CrossMatchTry = {
    answer: placed,
    duration: 0, // Not needed
  };
  return crossMatchFeedbackHtml(correctAnswer, answerTry);
}

const errorRow: DrillResultRowMap['CrossMatch'] = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const crossMatchStrategy: DrillStrategy<'CrossMatch'> = {
  kind: 'CrossMatch',
  speaksQuestion: true,
  speaksAnswer: false,
  errorRow,
  correctAnswer(item) {
    return item?.matching_values as MatchingValue[] | undefined;
  },
  isCorrect(answer, item) {
    if (answer === undefined || answer === null) return false;
    return isCorrect(answer, item?.matching_values);
  },
  feedbackAnswerHtml(answer, item) {
    return feedbackAnswerHtml(answer, item?.matching_values);
  },
  feedbackCorrectAnswer() {
    return '';
  },
  feedbackSubtitle(questionText, item) {
    return `${escapeText(questionText)}<p class="text-caption text-border q-my-none">${escapeText(item?.directions ?? '')}</p>`;
  },
  helpAnswer(item) {
    return crossMatchCorrectHtml(item?.matching_values);
  },
  spokenAnswer(answer) {
    // Not spoken (speaksAnswer: false); today's answerText returns the answer itself.
    return answer as unknown as string;
  },
  questionText(item) {
    return `${item?.sentence ?? ''}`;
  },
  resultRow(item, id, tries: Try[]) {
    return {
      id,
      source: item?.sentence,
      directions: item?.directions,
      correct: item?.matching_values,
      tries: (tries as unknown as CrossMatchTry[]).map((tryItem) => {
        return {
          duration: tryItem.duration,
          answer: tryItem.answer,
          isCorrect: tryItem.isCorrect,
          isHelp: tryItem.isHelp,
          ...(tryItem.isTimeout ? { isTimeout: true } : {}),
        };
      }),
    };
  },
};
